import { api } from './api.js';

/** Formato que o `PushManager` do navegador exige para a chave pública VAPID. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Pede permissão e inscreve o dispositivo para notificação push.
 *
 * Chamado a partir de um toque real (abrir o chat pela primeira vez) —
 * pedir permissão sem gesto do usuário é a receita clássica de o
 * navegador negar de cara ou o usuário recusar por reflexo.
 *
 * Sem chave pública configurada no servidor (`VAPID_PUBLIC_KEY` vazio),
 * ou sem suporte do navegador, sai em silêncio: notificação é reforço,
 * nunca bloqueia o uso do chat.
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!pushSupported()) return false;

  try {
    const { key } = await api<{ key: string | null }>('/api/push/public-key');
    if (!key) return false;

    if (Notification.permission === 'denied') return false;
    const permission =
      Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const registration = await navigator.serviceWorker.ready;
    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
      });
    }

    const json = sub.toJSON() as { endpoint: string; keys?: { p256dh: string; auth: string } };
    if (!json.keys) return false;

    await api('/api/push/subscribe', {
      body: { endpoint: json.endpoint, keys: json.keys },
    });
    return true;
  } catch {
    // Best effort: falha aqui nunca pode derrubar a tela que chamou.
    return false;
  }
}
